import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { exhibitionsApi, customersApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Loader2, Plus, ArrowLeft, Upload, Search, Users, User, CheckCircle2,
  MessageCircle, FileText, RefreshCw, X, Sparkles, Send, Languages, Camera,
  Eye, PhoneCall, Trash2, Wand2, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import Header from '../components/layout/Header';

const LANGS = [
  { value: 'en', label: 'English' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'ta', label: 'Tamil' },
];

const ExhibitionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ex, setEx] = useState(null);
  const [selections, setSelections] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listR, selR, dR, custR] = await Promise.all([
        exhibitionsApi.list(),
        exhibitionsApi.listSelections(id),
        exhibitionsApi.dashboard(id),
        customersApi.getAll(),
      ]);
      const one = (listR.data || []).find(x => x.id === id);
      if (!one) { toast.error('Exhibition not found'); navigate('/exhibitions'); return; }
      setEx(one);
      setSelections(Array.isArray(selR.data) ? selR.data : []);
      setDashboard(dR.data);
      setAllCustomers(Array.isArray(custR.data) ? custR.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !ex) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-neutral-950">
        <Header title="Exhibition" />
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="exhibition-detail-page">
      <Header title="Exhibition+" />
      <div className="max-w-screen-xl mx-auto px-3 md:px-8 py-4 md:py-6 space-y-5">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate('/exhibitions')}
          className="h-8 px-2 -ml-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> All exhibitions
        </Button>

        {/* Header */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border p-4 md:p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg md:text-2xl font-bold">{ex.name}</h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">
                {ex.dates_label} · {[ex.city, ex.venue].filter(Boolean).join(' · ') || '—'}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setWizardOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white h-10"
              data-testid="new-selection-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New Selection
            </Button>
          </div>

          {dashboard && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mt-4">
              <Tile label="Total" value={dashboard.total_selections} accent="text-slate-700" testid="tile-total" />
              <Tile label="Sent" value={dashboard.sent} accent="text-emerald-600" testid="tile-sent" />
              <Tile label="Failed" value={dashboard.failed} accent="text-rose-600" testid="tile-failed" />
              <Tile label="Draft" value={dashboard.draft} accent="text-amber-600" testid="tile-draft" />
              <Tile label="New CRM" value={dashboard.new_customers_captured} accent="text-violet-600" testid="tile-newcrm" />
            </div>
          )}
        </div>

        {/* Selections list */}
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            Selections <span className="ml-1 opacity-70">{selections.length}</span>
          </p>
          {selections.length === 0 ? (
            <div className="text-center py-14 rounded-2xl bg-white dark:bg-neutral-900 border border-dashed">
              <PhoneCall className="h-8 w-8 mx-auto mb-2 text-amber-500" />
              <p className="text-sm font-semibold">No selections yet</p>
              <p className="text-xs text-muted-foreground mt-1">Start when a customer finalises their picks at the booth.</p>
            </div>
          ) : (
            <div className="rounded-2xl bg-white dark:bg-neutral-900 border overflow-hidden divide-y">
              {selections.map(s => (
                <SelectionRow
                  key={s.id}
                  s={s}
                  onRefresh={load}
                  ex={ex}
                  allCustomers={allCustomers}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <NewSelectionWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        exhibition={ex}
        onDone={() => { setWizardOpen(false); load(); }}
      />
    </div>
  );
};

const Tile = ({ label, value, accent, testid }) => (
  <div className="p-3 rounded-xl border bg-slate-50 dark:bg-neutral-800/40" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
    <p className={cn("mt-0.5 text-lg md:text-xl font-bold tabular-nums", accent)}>{value ?? 0}</p>
  </div>
);

const statusChip = (status) => {
  if (status === 'sent') return { label: 'Sent', cls: 'border-emerald-300 text-emerald-700 bg-emerald-50' };
  if (status === 'failed') return { label: 'Failed', cls: 'border-rose-300 text-rose-700 bg-rose-50' };
  if (status === 'finalising') return { label: 'Sending…', cls: 'border-amber-300 text-amber-700 bg-amber-50' };
  return { label: 'Draft', cls: '' };
};

const SelectionRow = ({ s, onRefresh, ex, allCustomers }) => {
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const chip = statusChip(s.status);
  const doneCount = (s.send_results || []).filter(r => r.ok).length;
  const failedCount = (s.send_results || []).filter(r => !r.ok).length;
  const hasFinalPdf = !!s.merged_pdf;
  const isDraft = s.status === 'draft';
  const isSent = s.status === 'sent';

  const openPdf = () => {
    const url = exhibitionsApi.previewPdfUrl(ex.id, s.id);
    window.open(url, '_blank');
  };

  const generatePreview = async () => {
    if (!s.uploaded_pdf) {
      toast.error('Upload the selection PDF first (Edit → re-upload)');
      return;
    }
    setBusy(true);
    toast.info('Generating branded cover…', { duration: 2000 });
    try {
      await exhibitionsApi.generatePreview(ex.id, s.id);
      toast.success('Preview ready — tap the eye icon to view');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const deleteSelection = async () => {
    if (!window.confirm(`Delete this draft for ${s.customer_name}?`)) return;
    setBusy(true);
    try {
      await exhibitionsApi.deleteSelection(ex.id, s.id);
      toast.success('Draft deleted');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!window.confirm(`Resend to ${(s.contacts || []).map(c => c.phone).join(', ')}?`)) return;
    setBusy(true);
    try {
      await exhibitionsApi.resend(ex.id, s.id, {
        contacts: (s.contacts || []).map(c => c.phone),
        language: s.language || 'en',
      });
      toast.success('Resent');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="px-3 md:px-5 py-3.5 flex items-center gap-3 flex-wrap sm:flex-nowrap"
         data-testid={`selection-row-${s.id}`}>
      <div className="h-10 w-10 rounded-full bg-slate-100 dark:bg-neutral-800 flex items-center justify-center text-sm font-bold text-slate-600 flex-shrink-0">
        {(s.customer_name || '?')[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-sm font-semibold truncate">{s.customer_name}</p>
          <Badge variant="outline" className={cn("text-[10px]", chip.cls)}>{chip.label}</Badge>
          {isDraft && hasFinalPdf && (
            <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700 bg-violet-50">Preview ready</Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground truncate mt-0.5">
          {s.customer_primary_mobile || '—'} · by {s.created_by_name} · {new Date(s.created_at).toLocaleString()}
          {isSent && ` · ${doneCount}/${(s.contacts || []).length} delivered`}
          {failedCount > 0 && s.status === 'failed' && ` · ${failedCount} failed`}
        </p>
        {s.failure_reason && (
          <p className="text-[11px] text-rose-600 mt-1 whitespace-pre-wrap break-words">
            <strong>Why it failed:</strong> {s.failure_reason}
          </p>
        )}
        {/* Per-recipient error breakdown for failed sends */}
        {s.status === 'failed' && Array.isArray(s.send_results) && s.send_results.some(r => r.error) && (
          <div className="mt-1 space-y-0.5">
            {s.send_results.filter(r => r.error).map((r, i) => (
              <p key={i} className="text-[10px] text-rose-500 break-words">
                <span className="font-mono">+{r.phone}</span>
                {r.status_code ? ` (${r.status_code})` : ''}: {r.error}
              </p>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Preview / View PDF */}
        {hasFinalPdf ? (
          <Button size="sm" variant="outline" onClick={openPdf}
                  className="h-8 px-2.5 gap-1.5"
                  title="View branded PDF"
                  data-testid={`view-pdf-${s.id}`}>
            <Eye className="h-3.5 w-3.5" /> <span className="hidden sm:inline text-xs">View PDF</span>
          </Button>
        ) : isDraft && s.uploaded_pdf ? (
          <Button size="sm" variant="outline" onClick={generatePreview}
                  disabled={busy}
                  className="h-8 px-2.5 gap-1.5"
                  title="Generate the branded cover so you can preview the final PDF"
                  data-testid={`preview-${s.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline text-xs">Preview</span>
          </Button>
        ) : null}
        {/* Edit — drafts only */}
        {isDraft && (
          <Button size="sm" variant="ghost" onClick={() => setEditOpen(true)}
                  className="h-8 px-2"
                  title="Edit draft — swap customer or re-upload PDF"
                  data-testid={`edit-${s.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
        {/* Resend — sent only */}
        {isSent && (
          <Button size="sm" variant="ghost" onClick={resend} disabled={busy}
                  className="h-8 px-2" title="Resend the same PDF"
                  data-testid={`resend-${s.id}`}>
            <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} />
          </Button>
        )}
        {/* Delete — drafts (creator) or admin+ can wipe anything except sent-locked */}
        {!isSent && (
          <Button size="sm" variant="ghost" onClick={deleteSelection} disabled={busy}
                  className="h-8 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  title="Delete draft"
                  data-testid={`delete-${s.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <EditSelectionDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        selection={s}
        exhibition={ex}
        allCustomers={allCustomers}
        onSaved={onRefresh}
      />
    </div>
  );
};

// ─── Edit draft dialog ──────────────────────────────────────────────────────
const EditSelectionDialog = ({ open, onOpenChange, selection, exhibition, allCustomers = [], onSaved }) => {
  const [q, setQ] = useState('');
  const [pickedCustomerId, setPickedCustomerId] = useState(selection?.customer_id || null);
  const [pdfFile, setPdfFile] = useState(null);
  const [jewelleryFile, setJewelleryFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const pdfInputRef = useRef(null);
  const jewelleryInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ('');
      setPickedCustomerId(selection?.customer_id || null);
      setPdfFile(null);
      setJewelleryFile(null);
    }
  }, [open, selection?.id, selection?.customer_id]);

  const searchResults = useMemo(() => {
    const s = q.trim().toLowerCase();
    const source = allCustomers || [];
    if (!s) return source.slice(0, 20);
    return source.filter(c =>
      (c.company_name || '').toLowerCase().includes(s)
      || (c.contact_person_name || '').toLowerCase().includes(s)
      || (c.mobile || '').includes(s)
    ).slice(0, 20);
  }, [q, allCustomers]);

  const save = async () => {
    setBusy(true);
    try {
      // Swap customer if changed
      if (pickedCustomerId && pickedCustomerId !== selection.customer_id) {
        await exhibitionsApi.editSelection(exhibition.id, selection.id, {
          customer_id: pickedCustomerId,
        });
      }
      // Re-upload PDF if a new one was picked
      if (pdfFile) {
        await exhibitionsApi.uploadSelectionPdf(exhibition.id, selection.id, pdfFile);
      }
      // Re-upload jewellery ref if a new one was picked
      if (jewelleryFile) {
        await exhibitionsApi.uploadSelectionJewellery(exhibition.id, selection.id, jewelleryFile);
      }
      toast.success('Draft updated');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const nothingChanged = (
    (!pickedCustomerId || pickedCustomerId === selection?.customer_id)
    && !pdfFile && !jewelleryFile
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid="edit-selection-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit draft
          </DialogTitle>
          <DialogDescription>Swap the customer or replace the uploaded PDF. Any preview cover will be re-generated when you tap Preview / Send.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Customer</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder={selection?.customer_name || 'Search…'}
                className="pl-9 h-10"
                data-testid="edit-customer-search"
              />
            </div>
            <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto divide-y">
              {searchResults.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setPickedCustomerId(c.id)}
                  className={cn(
                    "w-full text-left p-2.5 flex items-center gap-2 hover:bg-muted/50",
                    pickedCustomerId === c.id && "bg-emerald-50 dark:bg-emerald-500/10"
                  )}
                  data-testid={`edit-pick-cust-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.company_name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {c.contact_person_name || '—'} · {c.mobile || 'no phone'}
                    </p>
                  </div>
                  {pickedCustomerId === c.id && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                </button>
              ))}
              {searchResults.length === 0 && (
                <div className="p-3 text-xs text-muted-foreground text-center">No matches</div>
              )}
            </div>
          </div>

          <div>
            <Label>Replace PDF (optional)</Label>
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              onChange={e => setPdfFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="edit-pdf-input"
            />
            <button
              type="button"
              onClick={() => pdfInputRef.current?.click()}
              className={cn(
                "mt-1 w-full h-16 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 transition-colors",
                pdfFile ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 hover:border-amber-400"
              )}
              data-testid="edit-pick-pdf-btn"
            >
              {pdfFile ? (
                <>
                  <FileText className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm truncate max-w-xs">{pdfFile.name}</span>
                </>
              ) : selection?.uploaded_pdf ? (
                <>
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">{selection.uploaded_pdf.filename || 'PDF on file'} — tap to replace</span>
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">Upload PDF</span>
                </>
              )}
            </button>
          </div>

          <div>
            <Label>Replace jewellery image (optional)</Label>
            <input
              ref={jewelleryInputRef}
              type="file"
              accept="image/*"
              onChange={e => setJewelleryFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="edit-jewellery-input"
            />
            <button
              type="button"
              onClick={() => jewelleryInputRef.current?.click()}
              className={cn(
                "mt-1 w-full h-16 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 transition-colors",
                jewelleryFile ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 hover:border-amber-400"
              )}
              data-testid="edit-pick-jewellery-btn"
            >
              {jewelleryFile ? (
                <>
                  <img src={URL.createObjectURL(jewelleryFile)} alt="jewellery" className="h-10 w-10 object-cover rounded" />
                  <span className="text-sm truncate max-w-xs">{jewelleryFile.name}</span>
                </>
              ) : selection?.jewellery_ref ? (
                <>
                  <Sparkles className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">{selection.jewellery_ref.filename || 'Image on file'} — tap to replace</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">Upload jewellery image (drives the AI cover)</span>
                </>
              )}
            </button>
            <p className="text-[10px] text-muted-foreground mt-1">
              Replacing the image will clear any preview cover — Preview / Send will regenerate.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || nothingChanged}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="save-edit-selection"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── New Selection Wizard ───────────────────────────────────────────────────
const NewSelectionWizard = ({ open, onOpenChange, exhibition, onDone }) => {
  const [step, setStep] = useState(1);  // 1: customer  2: pdf+contacts  3: send
  const [selectionId, setSelectionId] = useState(null);
  const [customer, setCustomer] = useState(null); // full customer object
  // Step 1 state
  const [tab, setTab] = useState('existing'); // existing | new
  const [q, setQ] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);
  const [nv, setNv] = useState({
    company_name: '', contact_person_name: '', contact_designation: 'operations',
    mobile: '', email: '', city: '', gstin: '',
  });
  // Step 2 state
  const [pdfFile, setPdfFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [jewelleryFile, setJewelleryFile] = useState(null);
  const [contacts, setContacts] = useState([]);  // [{phone, source, checked}]
  const [customPhone, setCustomPhone] = useState('');
  const [language, setLanguage] = useState('en');
  const [busy, setBusy] = useState(false);

  const pdfInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const jewelleryInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep(1); setSelectionId(null); setCustomer(null);
      setTab('existing'); setQ(''); setPdfFile(null); setLogoFile(null);
      setJewelleryFile(null);
      setContacts([]); setCustomPhone(''); setLanguage('en');
      setNv({ company_name: '', contact_person_name: '', contact_designation: 'operations',
              mobile: '', email: '', city: '', gstin: '' });
      // Pre-load customers for search
      customersApi.getAll().then(r => setAllCustomers(r.data || [])).catch(() => {});
    }
  }, [open]);

  const searchResults = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return allCustomers.slice(0, 25);
    return allCustomers.filter(c =>
      (c.company_name || '').toLowerCase().includes(s)
      || (c.contact_person_name || '').toLowerCase().includes(s)
      || (c.mobile || '').includes(s)
      || (c.additional_phones || []).some(p =>
        (p?.name || '').toLowerCase().includes(s)
        || (p?.label || '').toLowerCase().includes(s)
        || (p?.phone || '').includes(s)
      )
    ).slice(0, 25);
  }, [q, allCustomers]);

  const createFor = async (payload) => {
    setBusy(true);
    try {
      const r = await exhibitionsApi.createSelection(exhibition.id, {
        exhibition_id: exhibition.id, ...payload,
      });
      setSelectionId(r.data.id);
      // Fetch customer for logo/contact info
      try {
        const c = await customersApi.get(r.data.customer_id);
        setCustomer(c.data);
      } catch { setCustomer({ id: r.data.customer_id, company_name: r.data.customer_name, mobile: r.data.customer_primary_mobile }); }
      // Prefill contacts from customer's phones
      const defaults = [];
      if (r.data.customer_primary_mobile) {
        defaults.push({ phone: r.data.customer_primary_mobile, source: 'primary', checked: true });
      }
      setContacts(defaults);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start selection');
    } finally { setBusy(false); }
  };

  // Once we have a customer object, fold in additional_phones into contacts
  useEffect(() => {
    if (!customer) return;
    setContacts(prev => {
      const seen = new Set(prev.map(c => c.phone));
      const extras = (customer.additional_phones || [])
        .filter(p => p?.phone && !seen.has(p.phone))
        .map(p => ({ phone: p.phone, source: p.label || p.name || 'contact', checked: false }));
      return [...prev, ...extras];
    });
  }, [customer]);

  const step1Existing = async () => {
    if (!customer) { toast.error('Pick a customer first'); return; }
    await createFor({ customer_id: customer.id });
  };
  const step1New = async () => {
    if (!nv.company_name.trim() || !nv.contact_person_name.trim() || !nv.mobile.trim()) {
      toast.error('Company, contact person and mobile are required');
      return;
    }
    await createFor({ new_visitor: nv });
  };

  const uploadPdf = async () => {
    if (!pdfFile) { toast.error('Upload the selection PDF'); return false; }
    try {
      await exhibitionsApi.uploadSelectionPdf(exhibition.id, selectionId, pdfFile);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF upload failed');
      return false;
    }
  };

  const uploadLogoIfNeeded = async () => {
    if (!logoFile || !customer?.id) return true;
    try {
      await exhibitionsApi.uploadCustomerLogo(customer.id, logoFile);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Logo upload failed (proceeding without it)');
      return true;  // non-fatal
    }
  };

  const uploadJewelleryIfNeeded = async () => {
    if (!jewelleryFile) return true;
    try {
      await exhibitionsApi.uploadSelectionJewellery(exhibition.id, selectionId, jewelleryFile);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Jewellery image upload failed');
      return false;  // fatal — no fallback selection piece
    }
  };

  const addCustomContact = () => {
    const p = customPhone.trim().replace(/[+\s-]/g, '');
    if (!p || p.length < 10) { toast.error('Enter a valid phone'); return; }
    if (contacts.some(c => c.phone === p)) { toast.error('Already in list'); return; }
    setContacts(prev => [...prev, { phone: p, source: 'custom', checked: true }]);
    setCustomPhone('');
  };

  const toggleContact = (phone) => {
    setContacts(prev => prev.map(c => c.phone === phone ? { ...c, checked: !c.checked } : c));
  };

  const finalise = async () => {
    const picked = contacts.filter(c => c.checked).map(c => c.phone);
    if (picked.length === 0) { toast.error('Pick at least one recipient'); return; }
    setBusy(true);
    try {
      // Ensure PDF is uploaded (step 2 handled it) — logo + jewellery upload if provided
      const okLogo = await uploadLogoIfNeeded();
      if (!okLogo) return;
      const okJ = await uploadJewelleryIfNeeded();
      if (!okJ) return;
      const r = await exhibitionsApi.finalise(exhibition.id, selectionId, {
        contacts: picked, language,
      });
      if (r.data.status === 'sent') {
        const ok = (r.data.send_results || []).filter(x => x.ok).length;
        toast.success(`Sent to ${ok} of ${picked.length} — cover generated & delivered 🎉`);
      } else {
        toast.error(r.data.failure_reason || 'Send failed');
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setBusy(false); }
  };

  const goStep3 = async () => {
    if (!pdfFile) { toast.error('Upload the selection PDF'); return; }
    if (!jewelleryFile) { toast.error('Upload the jewellery-piece image — it drives the AI cover'); return; }
    setBusy(true);
    try {
      const ok = await uploadPdf();
      if (!ok) return;
      // Upload jewellery ref now so it lands on the DB before any Preview call
      const okJ = await uploadJewelleryIfNeeded();
      if (!okJ) return;
      setJewelleryFile(null);  // already uploaded, don't re-upload on Send
      setStep(3);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="new-selection-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" /> New Selection · {exhibition?.name}
          </DialogTitle>
          <DialogDescription>Step {step} of 3</DialogDescription>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex items-center gap-2 pb-2">
          {[1, 2, 3].map(n => (
            <div key={n} className={cn("h-1.5 flex-1 rounded-full",
              step >= n ? "bg-amber-500" : "bg-slate-200 dark:bg-neutral-800")} />
          ))}
        </div>

        {/* ── STEP 1 · Customer ── */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={tab === 'existing' ? 'default' : 'outline'}
                onClick={() => setTab('existing')}
                className={cn("h-11", tab === 'existing' && "bg-slate-900 text-white hover:bg-slate-800")}
                data-testid="tab-existing"
              >
                <Users className="h-4 w-4 mr-1.5" /> Existing customer
              </Button>
              <Button
                type="button"
                variant={tab === 'new' ? 'default' : 'outline'}
                onClick={() => setTab('new')}
                className={cn("h-11", tab === 'new' && "bg-slate-900 text-white hover:bg-slate-800")}
                data-testid="tab-new"
              >
                <User className="h-4 w-4 mr-1.5" /> New walk-in
              </Button>
            </div>

            {tab === 'existing' ? (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Search by company, contact or phone…"
                    className="pl-9 h-11"
                    data-testid="customer-search"
                    autoFocus
                  />
                </div>
                <div className="border rounded-lg max-h-64 overflow-y-auto divide-y">
                  {searchResults.length === 0 ? (
                    <div className="p-3 text-xs text-muted-foreground text-center">No matches</div>
                  ) : searchResults.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCustomer(c)}
                      className={cn(
                        "w-full text-left p-2.5 flex items-center gap-2 hover:bg-muted/50",
                        customer?.id === c.id && "bg-emerald-50 dark:bg-emerald-500/10"
                      )}
                      data-testid={`pick-cust-${c.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.company_name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {c.contact_person_name || '—'} · {c.mobile || 'no phone'}
                        </p>
                      </div>
                      {customer?.id === c.id && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    </button>
                  ))}
                </div>
                <Button
                  onClick={step1Existing}
                  disabled={!customer || busy}
                  className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="next-step-1-existing"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                  Continue with {customer?.company_name || 'selected customer'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-[11px] text-muted-foreground">
                  Full KYC-1 — this walk-in gets added to your CRM tagged as an exhibition capture.
                </p>
                <div>
                  <Label>Company name *</Label>
                  <Input value={nv.company_name} onChange={e => setNv(p => ({ ...p, company_name: e.target.value }))}
                         className="h-11 mt-1" data-testid="nv-company" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Contact person *</Label>
                    <Input value={nv.contact_person_name}
                           onChange={e => setNv(p => ({ ...p, contact_person_name: e.target.value }))}
                           className="h-11 mt-1" data-testid="nv-contact" />
                  </div>
                  <div>
                    <Label>Designation</Label>
                    <Select value={nv.contact_designation}
                            onValueChange={v => setNv(p => ({ ...p, contact_designation: v }))}>
                      <SelectTrigger className="h-11 mt-1" data-testid="nv-designation"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operations">Operations</SelectItem>
                        <SelectItem value="accounts">Accounts</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>WhatsApp number *</Label>
                  <Input value={nv.mobile} onChange={e => setNv(p => ({ ...p, mobile: e.target.value }))}
                         placeholder="919876543210" className="h-11 mt-1 font-mono" data-testid="nv-mobile" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Email</Label>
                    <Input value={nv.email} onChange={e => setNv(p => ({ ...p, email: e.target.value }))}
                           className="h-11 mt-1" data-testid="nv-email" />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={nv.city} onChange={e => setNv(p => ({ ...p, city: e.target.value }))}
                           className="h-11 mt-1" data-testid="nv-city" />
                  </div>
                </div>
                <div>
                  <Label>GSTIN</Label>
                  <Input value={nv.gstin} onChange={e => setNv(p => ({ ...p, gstin: e.target.value }))}
                         className="h-11 mt-1" data-testid="nv-gstin" />
                </div>
                <Button
                  onClick={step1New}
                  disabled={busy}
                  className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="next-step-1-new"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                  Add customer & continue
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 2 · PDF + Logo ── */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 dark:bg-neutral-800/40 border p-3 text-sm">
              Customer: <strong>{customer?.company_name}</strong>
            </div>

            <div>
              <Label>Selection PDF *</Label>
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf"
                onChange={e => setPdfFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="pdf-input"
              />
              <button
                type="button"
                onClick={() => pdfInputRef.current?.click()}
                className={cn(
                  "mt-1 w-full h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors",
                  pdfFile ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 hover:border-amber-400"
                )}
                data-testid="pick-pdf-btn"
              >
                {pdfFile ? (
                  <>
                    <FileText className="h-6 w-6 text-emerald-600" />
                    <p className="text-sm font-medium truncate max-w-full px-4">{pdfFile.name}</p>
                    <p className="text-[10px] text-muted-foreground">{Math.round(pdfFile.size / 1024)} KB · tap to replace</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-muted-foreground" />
                    <p className="text-sm">Tap to upload the customer&apos;s selection PDF</p>
                  </>
                )}
              </button>
            </div>

            <div>
              <Label>Customer logo <span className="text-muted-foreground font-normal">(one-time — used for AI cover)</span></Label>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={e => setLogoFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="logo-input"
              />
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className={cn(
                  "mt-1 w-full h-20 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 transition-colors",
                  logoFile ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 hover:border-amber-400"
                )}
                data-testid="pick-logo-btn"
              >
                {logoFile ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="text-sm">{logoFile.name}</span>
                  </>
                ) : customer?.logo ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <span className="text-sm">Logo already on file — tap to replace</span>
                  </>
                ) : (
                  <>
                    <Camera className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm">Tap to upload customer logo (PNG/JPG)</span>
                  </>
                )}
              </button>
            </div>

            <div>
              <Label>
                Jewellery piece image *
                <span className="text-muted-foreground font-normal ml-1">(AI recreates this piece on the cover)</span>
              </Label>
              <input
                ref={jewelleryInputRef}
                type="file"
                accept="image/*"
                onChange={e => setJewelleryFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="jewellery-input"
              />
              <button
                type="button"
                onClick={() => jewelleryInputRef.current?.click()}
                className={cn(
                  "mt-1 w-full h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors",
                  jewelleryFile ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 hover:border-amber-400"
                )}
                data-testid="pick-jewellery-btn"
              >
                {jewelleryFile ? (
                  <>
                    {/* preview thumb */}
                    <img
                      src={URL.createObjectURL(jewelleryFile)}
                      alt="jewellery preview"
                      className="h-14 w-14 object-cover rounded-md"
                    />
                    <p className="text-xs text-emerald-700 truncate max-w-full px-4">{jewelleryFile.name}</p>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-6 w-6 text-muted-foreground" />
                    <p className="text-sm">Tap to upload a photo of the jewellery piece</p>
                    <p className="text-[10px] text-muted-foreground">Nano Banana will recreate it on the branded cover</p>
                  </>
                )}
              </button>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} disabled={busy} className="flex-1">
                Back
              </Button>
              <Button
                onClick={goStep3}
                disabled={!pdfFile || !jewelleryFile || busy}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="next-step-2"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 3 · Recipients & Send ── */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 dark:bg-neutral-800/40 border p-3 text-sm">
              {customer?.company_name} · PDF ready · logo {logoFile || customer?.logo ? 'ready' : 'placeholder will be used'} · jewellery ref ready
            </div>

            <div>
              <Label className="flex items-center gap-1.5">
                <MessageCircle className="h-3.5 w-3.5" /> Recipients
              </Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                We&apos;ll WhatsApp the branded PDF to every ticked number.
              </p>
              <div className="mt-2 space-y-1.5 max-h-52 overflow-y-auto border rounded-lg p-2">
                {contacts.length === 0 && (
                  <div className="text-xs text-muted-foreground p-2">
                    No numbers on file — add one below.
                  </div>
                )}
                {contacts.map(c => (
                  <label
                    key={c.phone}
                    className="flex items-center gap-2 p-2 rounded hover:bg-muted/40 cursor-pointer"
                    data-testid={`contact-${c.phone}`}
                  >
                    <Checkbox
                      checked={c.checked}
                      onCheckedChange={() => toggleContact(c.phone)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono">+{c.phone}</p>
                      <p className="text-[10px] text-muted-foreground">{c.source}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={customPhone}
                  onChange={e => setCustomPhone(e.target.value)}
                  placeholder="Add another phone (e.g. 919876543210)"
                  className="h-10 font-mono"
                  data-testid="custom-phone"
                />
                <Button variant="outline" onClick={addCustomContact} className="h-10"
                        data-testid="add-custom-phone">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-1.5">
                <Languages className="h-3.5 w-3.5" /> Template language
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="h-11 mt-1" data-testid="language-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGS.map(l => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)} disabled={busy} className="flex-1">
                Back
              </Button>
              <Button
                onClick={finalise}
                disabled={busy || contacts.filter(c => c.checked).length === 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11"
                data-testid="submit-finalise"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                Generate cover & Send
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground text-center">
              AI cover generation takes ~10–20 seconds.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExhibitionDetail;
